<template>
  <button
    @click="generarPdf"
    type="button"
    class="btn btn-primary btn-estado btn-certificado-deuda ms-2"
    :disabled="!puedeGenerar"
    :title="mensajeBloqueo"
  >
    <i class="fa-solid fa-file-pdf me-2"></i>Certificado de deuda
  </button>
</template>

<script>
import jsPDF from "jspdf";
import headerImage from "@/assets/Header2.png";

export default {
  name: "PdfCertificacionDeuda",
  props: {
    hacerPagoInfo: {
      type: Object,
      default: () => ({}),
    },
    fechaUltimoPago: {
      type: String,
      default: "",
    },
    fechaPagado: {
      type: String,
      default: "",
    },
    noPrestamo: {
      type: String,
      default: "",
    },
  },
  computed: {
    tieneInformacion() {
      return Boolean(this.hacerPagoInfo?.nombres || this.hacerPagoInfo?.dni);
    },
    hoyHonduras() {
      const partes = new Intl.DateTimeFormat("en-CA", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: "America/Tegucigalpa",
      }).formatToParts(new Date());
      const valores = partes.reduce((acc, parte) => {
        acc[parte.type] = parte.value;
        return acc;
      }, {});

      return `${valores.year}-${valores.month}-${valores.day}`;
    },
    fechaPagoValida() {
      return Boolean(this.fechaPagado) && this.fechaPagado >= this.hoyHonduras;
    },
    puedeGenerar() {
      return this.tieneInformacion && this.fechaPagoValida;
    },
    mensajeBloqueo() {
      if (!this.tieneInformacion) {
        return "No hay información del crédito para generar el certificado.";
      }

      if (!this.fechaPagoValida) {
        return "La fecha de pago debe ser mayor o igual a hoy según horario de Honduras.";
      }

      return "Generar certificado de deuda";
    },
  },
  methods: {
    numero(valor) {
      const numero = Number(valor);
      return Number.isFinite(numero) ? numero : 0;
    },
    texto(valor, fallback = "-") {
      if (valor === null || valor === undefined || valor === "") {
        return fallback;
      }

      return String(valor);
    },
    nombreBeneficiario() {
      return `${this.texto(this.hacerPagoInfo?.nombres, "")} ${this.texto(
        this.hacerPagoInfo?.apellidos,
        ""
      )}`
        .replace(/\s+/g, " ")
        .trim();
    },
    formatoMoneda(valor) {
      return `L. ${new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(this.numero(valor))}`;
    },
    formatoFecha(valor) {
      if (!valor) return "-";

      const fecha = new Date(valor);
      if (Number.isNaN(fecha.getTime()) || fecha.getUTCFullYear() < 1902) {
        return "-";
      }

      const dia = ("0" + fecha.getUTCDate()).slice(-2);
      const mes = ("0" + (fecha.getUTCMonth() + 1)).slice(-2);
      const anio = fecha.getUTCFullYear();

      return `${dia}/${mes}/${anio}`;
    },
    formatoFechaLarga(valor = new Date()) {
      return new Intl.DateTimeFormat("es-HN", {
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: "America/Tegucigalpa",
      }).format(new Date(valor));
    },
    formatoFechaPagoLarga(valor) {
      if (!valor) return this.formatoFechaLarga();

      const [anio, mes, dia] = valor.slice(0, 10).split("-").map(Number);
      if (!anio || !mes || !dia) return this.formatoFechaLarga(valor);

      return new Intl.DateTimeFormat("es-HN", {
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      }).format(new Date(Date.UTC(anio, mes - 1, dia)));
    },
    fechaMora() {
      const valor =
        this.hacerPagoInfo?.fechaCuotaSinPagar ||
        this.fechaUltimoPago ||
        this.hacerPagoInfo?.fechaVencimiento;

      if (!valor) return "-";

      const fecha = new Date(valor);
      if (Number.isNaN(fecha.getTime()) || fecha.getUTCFullYear() < 1902) {
        return "-";
      }

      return new Intl.DateTimeFormat("es-HN", {
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      }).format(fecha);
    },
    totalPagado() {
      const capitalAcumulado = this.numero(this.hacerPagoInfo?.capitalAcumulado);
      const totalInteresPagado = this.numero(
        this.hacerPagoInfo?.totalInteresPagado
      );
      const totalMoraPagada = this.numero(this.hacerPagoInfo?.totalMoraPagada);
      const totalArrInteresPagado = this.numero(
        this.hacerPagoInfo?.totalArrInteresPagado
      );

      return (
        capitalAcumulado +
        totalInteresPagado +
        totalMoraPagada +
        totalArrInteresPagado
      );
    },
    async agregarEncabezado(doc) {
      await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const docWidth = doc.internal.pageSize.getWidth();
          doc.addImage(img, "PNG", 0, 0, docWidth, 30);
          resolve();
        };
        img.onerror = resolve;
        img.src = headerImage;
      });
    },
    escribirCentrado(doc, texto, y, fontSize = 12, fontStyle = "normal") {
      doc.setFont("times", fontStyle);
      doc.setFontSize(fontSize);
      doc.text(texto, doc.internal.pageSize.getWidth() / 2, y, {
        align: "center",
      });
    },
    escribirParrafo(doc, texto, x, y, ancho, lineHeight = 4.8) {
      const lineas = doc.splitTextToSize(texto, ancho);
      doc.text(lineas, x, y);
      return y + lineas.length * lineHeight;
    },
    escribirParrafoJustificado(doc, texto, x, y, ancho, lineHeight = 4.8) {
      const lineas = doc.splitTextToSize(texto, ancho);

      lineas.forEach((linea, index) => {
        const ultimaLinea = index === lineas.length - 1;
        const palabras = linea.trim().split(/\s+/);

        if (ultimaLinea || palabras.length === 1) {
          doc.text(linea, x, y);
        } else {
          const anchoPalabras = palabras.reduce(
            (total, palabra) => total + doc.getTextWidth(palabra),
            0
          );
          const espacio = (ancho - anchoPalabras) / (palabras.length - 1);
          let cursorX = x;

          palabras.forEach((palabra) => {
            doc.text(palabra, cursorX, y);
            cursorX += doc.getTextWidth(palabra) + espacio;
          });
        }

        y += lineHeight;
      });

      return y;
    },
    escribirFila(doc, etiqueta, valor, x, y) {
      doc.setFontSize(10.5);
      doc.setFont("times", "bold");
      doc.text(etiqueta, x, y);
      doc.setFont("times", "normal");
      doc.text(valor, x + 82, y);
    },
    nombreArchivo(nombre) {
      return nombre
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9 -]/g, "")
        .replace(/\s+/g, " ")
        .trim();
    },
    async generarPdf() {
      if (!this.puedeGenerar) return;

      const doc = new jsPDF("p", "mm", "letter");
      const margenX = 22;
      const anchoTexto = 172;
      const bodyFontSize = 10.5;
      const lineHeight = 4.8;
      const nombre = this.nombreBeneficiario();
      const fechaPagoSeleccionada = this.formatoFechaPagoLarga(this.fechaPagado);
      const numeroPrestamo = this.texto(
        this.noPrestamo || this.hacerPagoInfo?.noPrestamo
      );

      await this.agregarEncabezado(doc);

      doc.setProperties({
        title: `Certificado de deuda - ${nombre}`,
        subject: "Certificado de deuda",
      });

      doc.setTextColor(0, 0, 0);
      doc.setFont("times", "bold");
      doc.setFontSize(15);
      doc.text("CERTIFICADO DE DEUDA", doc.internal.pageSize.getWidth() / 2, 40, {
        align: "center",
      });

      doc.setFont("times", "normal");
      doc.setFontSize(bodyFontSize);
      doc.text(`Tegucigalpa M.D.C., ${fechaPagoSeleccionada}`, 194, 47, {
        align: "right",
      });

      let y = 56;
      doc.text("Señores:", margenX, y);
      y += lineHeight;
      doc.setFont("times", "bold");
      doc.text("JUZGADO CIVIL", margenX, y);
      y += lineHeight;
      doc.setFont("times", "normal");
      doc.text("Presente.", margenX, y);

      y += 9;
      const intro =
        "Yo, DAVID ALEXANDER GODOY MELENDEZ, hondureño, mayor de edad, Contador Público debidamente colegiado bajo el número 2025-11-4813, portador del Documento Nacional de Identificación (DNI) 0801-1999-05240 y Registro Tributario Nacional (RTN) 08011999052400, en mi condición de profesional responsable de la administración contable de la ASOCIACIÓN HONDUREÑA PARA EL DESARROLLO INTEGRAL COMUNITARIO, con RTN 08269016867779, por medio del presente:";
      y = this.escribirParrafoJustificado(
        doc,
        intro,
        margenX,
        y,
        anchoTexto,
        lineHeight
      );

      y += 4;
      doc.setFont("times", "bold");
      doc.setFontSize(bodyFontSize);
      doc.text("CERTIFICO", margenX, y);

      y += 7;
      doc.setFont("times", "normal");
      doc.setFontSize(bodyFontSize);
      const certifico =
        `Que el Señor ${nombre}, con Documento de Identificación (DNI) ${this.texto(
          this.hacerPagoInfo?.dni
        )} mantiene una obligación financiera pendiente con esta institución, lo cual entro en mora desde el ${this.fechaMora()}, correspondiente al préstamo número ${numeroPrestamo}, conforme a los siguientes términos:`;
      y = this.escribirParrafoJustificado(
        doc,
        certifico,
        margenX,
        y,
        anchoTexto,
        lineHeight
      );

      y += 5;
      const filas = [
        [
          "Fecha de desembolso del crédito:",
          this.formatoFecha(this.hacerPagoInfo?.fechaDesembolso),
        ],
        [
          "Monto aprobado del crédito:",
          this.formatoMoneda(this.hacerPagoInfo?.montoAprobado),
        ],
        ["Pagos efectuados a la fecha:", this.formatoMoneda(this.totalPagado())],
        [
          "Total interés pagado:",
          this.formatoMoneda(this.hacerPagoInfo?.totalInteresPagado),
        ],
        [
          "Total mora pagada:",
          this.formatoMoneda(this.hacerPagoInfo?.totalMoraPagada),
        ],
        [
          "Capital atrasado:",
          this.formatoMoneda(this.hacerPagoInfo?.capitalDebePagar),
        ],
        [
          "Total para cancelar el crédito:",
          this.formatoMoneda(this.hacerPagoInfo?.totalMontoCancelar),
        ],
      ];

      filas.forEach(([etiqueta, valor]) => {
        this.escribirFila(doc, etiqueta, valor, margenX, y);
        y += 6;
      });

      y += 5;
      y = this.escribirParrafo(
        doc,
        "La presente certificación se extiende a solicitud de parte interesada para los fines legales que estime convenientes.",
        margenX,
        y,
        anchoTexto,
        lineHeight
      );

      y += 2;
      y = this.escribirParrafoJustificado(
        doc,
        `Sin otro particular, suscribo el presente en la ciudad de Tegucigalpa, Municipio del Distrito Central, a los ${fechaPagoSeleccionada}.`,
        margenX,
        y,
        anchoTexto,
        lineHeight
      );

      const firmaY = 198;
      doc.setFont("times", "bold");
      doc.setFontSize(bodyFontSize);
      doc.text("Atentamente,", margenX, firmaY + 6);

      doc.setFont("times", "normal");
      doc.text("Firma y sello:", margenX, firmaY + 18);

      doc.line(margenX, firmaY + 32, margenX + 70, firmaY + 32);
      doc.setFont("times", "bold");
      doc.setFontSize(bodyFontSize);
      doc.text("David Alexander Godoy Meléndez", margenX, firmaY + 38);
      doc.setFont("times", "normal");
      doc.text("Contador Público", margenX, firmaY + 43);
      doc.text("Colegiación No. 2025-11-4813", margenX, firmaY + 48);

      doc.setFont("times", "normal");
      doc.setFontSize(8.5);
      doc.text(
        "Colonia Rubén Darío, Casa 0592, Tegucigalpa Honduras",
        doc.internal.pageSize.getWidth() / 2,
        258,
        { align: "center" }
      );
      doc.text(
        "Correo electrónico: info@ahdecohn.org",
        doc.internal.pageSize.getWidth() / 2,
        263,
        { align: "center" }
      );
      doc.text("Tel. (504) 2242-5780", doc.internal.pageSize.getWidth() / 2, 268, {
        align: "center",
      });
      doc.text("www.ahdecohn.org", doc.internal.pageSize.getWidth() / 2, 273, {
        align: "center",
      });

      doc.save(`Certificado de deuda-${this.nombreArchivo(nombre)}.pdf`);
    },
  },
};
</script>

<style scoped>
.btn-certificado-deuda,
.btn-certificado-deuda:hover,
.btn-certificado-deuda:focus,
.btn-certificado-deuda:active,
.btn-certificado-deuda:focus-visible,
.btn-certificado-deuda:disabled {
  color: #ffffff !important;
}

.btn-certificado-deuda:active,
.btn-certificado-deuda:focus {
  background-color: #0a58ca !important;
  border-color: #0a53be !important;
}
</style>
